import React, { useState, useEffect } from 'react';
import { Card, Spin, message, Descriptions, Typography, Row, Col, Divider, Button, Form, Input, InputNumber, Rate } from 'antd';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const { Title, Paragraph } = Typography;

const StudentArchive = ({ user }) => {
  const [archiveData, setArchiveData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form] = Form.useForm();
  const { apiClient } = useAuth();
  const navigate = useNavigate();
  const { babyId } = useParams();

  useEffect(() => {
    if (babyId) {
      fetchArchiveData(babyId);
    }
  }, [babyId]);

  const fetchArchiveData = async (id) => {
    setLoading(true);
    try {
      const response = await apiClient.get(`/sports-server-prod/taller.api.v1/growth/queryById`, {
        params: {
          babyId: id,
          searchType: 'first'
        }
      });

      if (response.data.code === 200 && response.data.data && response.data.data.length > 0) {
        setArchiveData(response.data.data[0]);
        form.setFieldsValue(response.data.data[0]);
      } else {
        message.error(response.data.msg || '获取档案数据失败');
      }
    } catch (error) {
      console.error('获取档案数据错误:', error);
      message.error('获取档案数据失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const payload = {
        ...values,
        babyId: babyId
      };

      const response = await apiClient.post(`/sports-server-prod/taller.api.v1/growth/add`, payload);

      if (response.data.code === 200) {
        message.success('保存成功');
        setEditing(false);
        fetchArchiveData(babyId);
      } else {
        message.error(response.data.msg || '保存失败');
      }
    } catch (error) {
      console.error('保存档案数据错误:', error);
      message.error('保存失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const renderBasicInfo = () => {
    if (!archiveData) return null;

    return (
      <Card title="基本信息" style={{ marginBottom: 16 }}>
        <Descriptions column={{ xs: 1, sm: 2, md: 3 }} bordered>
          <Descriptions.Item label="身高">{archiveData.height} cm</Descriptions.Item>
          <Descriptions.Item label="体重">{archiveData.weight} kg</Descriptions.Item>
          <Descriptions.Item label="BMI">{archiveData.bmi}</Descriptions.Item>
          {!editing ? (
            <>
              <Descriptions.Item label="身高百分位">{archiveData.heightTh}%</Descriptions.Item>
              <Descriptions.Item label="体重百分位">{archiveData.weightTh}%</Descriptions.Item>
              <Descriptions.Item label="父亲身高">{archiveData.heightF} cm</Descriptions.Item>
              <Descriptions.Item label="母亲身高">{archiveData.heightM} cm</Descriptions.Item>
              <Descriptions.Item label="骨龄">{archiveData.boneAge} 岁</Descriptions.Item>
              <Descriptions.Item label="遗传身高">{archiveData.geneticHeight} cm</Descriptions.Item>
              <Descriptions.Item label="预测成年身高">{archiveData.futureHeight} cm</Descriptions.Item>
            </>
          ) : (
            <>
              <Descriptions.Item label="身高百分位">
                <Form.Item name="heightTh" noStyle>
                  <InputNumber addonAfter="%" style={{ width: '100%' }} />
                </Form.Item>
              </Descriptions.Item>
              <Descriptions.Item label="体重百分位">
                <Form.Item name="weightTh" noStyle>
                  <InputNumber addonAfter="%" style={{ width: '100%' }} />
                </Form.Item>
              </Descriptions.Item>
              <Descriptions.Item label="父亲身高">
                <Form.Item name="heightF" noStyle>
                  <InputNumber addonAfter="cm" style={{ width: '100%' }} />
                </Form.Item>
              </Descriptions.Item>
              <Descriptions.Item label="母亲身高">
                <Form.Item name="heightM" noStyle>
                  <InputNumber addonAfter="cm" style={{ width: '100%' }} />
                </Form.Item>
              </Descriptions.Item>
              <Descriptions.Item label="骨龄">
                <Form.Item name="boneAge" noStyle>
                  <InputNumber addonAfter="岁" style={{ width: '100%' }} />
                </Form.Item>
              </Descriptions.Item>
              <Descriptions.Item label="遗传身高">
                <Form.Item name="geneticHeight" noStyle>
                  <InputNumber addonAfter="cm" style={{ width: '100%' }} />
                </Form.Item>
              </Descriptions.Item>
              <Descriptions.Item label="预测成年身高">
                <Form.Item name="futureHeight" noStyle>
                  <InputNumber addonAfter="cm" style={{ width: '100%' }} />
                </Form.Item>
              </Descriptions.Item>
            </>
          )}
        </Descriptions>
      </Card>
    );
  };

  const renderAssessmentResults = () => {
    if (!archiveData) return null;

    return (
      <Card title="测评结果" style={{ marginBottom: 16 }}>
        {!editing ? (
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Title level={5}>骨龄评测</Title>
              <Paragraph>{archiveData.insResultBoneAge}</Paragraph>
            </Col>
            <Col span={24}>
              <Title level={5}>体态评测</Title>
              <Paragraph>{archiveData.insResultPosture}</Paragraph>
            </Col>
            <Col span={24}>
              <Title level={5}>运动能力评测</Title>
              <Paragraph>{archiveData.insResultLocomotion}</Paragraph>
            </Col>
            <Col span={24}>
              <Title level={5}>其他评测</Title>
              <Paragraph>{archiveData.insResultOther || '暂无'}</Paragraph>
            </Col>
          </Row>
        ) : (
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Form.Item name="insResultBoneAge" label="骨龄评测">
                <Input.TextArea autoSize={{ minRows: 2, maxRows: 6 }} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="insResultPosture" label="体态评测">
                <Input.TextArea autoSize={{ minRows: 2, maxRows: 6 }} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="insResultLocomotion" label="运动能力评测">
                <Input.TextArea autoSize={{ minRows: 2, maxRows: 6 }} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="insResultOther" label="其他评测">
                <Input.TextArea autoSize={{ minRows: 2, maxRows: 6 }} />
              </Form.Item>
            </Col>
          </Row>
        )}
      </Card>
    );
  };

  const renderPersonalInfo = () => {
    if (!archiveData) return null;

    return (
      <Card title="个性信息" style={{ marginBottom: 16 }}>
        {!editing ? (
          <Descriptions column={{ xs: 1, sm: 2 }} bordered>
            <Descriptions.Item label="性格">{archiveData.personality}</Descriptions.Item>
            <Descriptions.Item label="爱好">{archiveData.hobby}</Descriptions.Item>
            <Descriptions.Item label="运动水平">
              <Rate disabled defaultValue={archiveData.locomotionLevel} />
            </Descriptions.Item>
            <Descriptions.Item label="专注力水平">
              <Rate disabled defaultValue={archiveData.focusPowerLevel} />
            </Descriptions.Item>
          </Descriptions>
        ) : (
          <Descriptions column={{ xs: 1, sm: 2 }} bordered>
            <Descriptions.Item label="性格">
              <Form.Item name="personality" noStyle>
                <Input />
              </Form.Item>
            </Descriptions.Item>
            <Descriptions.Item label="爱好">
              <Form.Item name="hobby" noStyle>
                <Input />
              </Form.Item>
            </Descriptions.Item>
            <Descriptions.Item label="运动水平">
              <Form.Item name="locomotionLevel" noStyle>
                <Rate />
              </Form.Item>
            </Descriptions.Item>
            <Descriptions.Item label="专注力水平">
              <Form.Item name="focusPowerLevel" noStyle>
                <Rate />
              </Form.Item>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Card>
    );
  };

  // 身高预测数据处理
  const getHeightPredictionData = () => {
    if (!archiveData) return [];
    
    const data = [];
    if (archiveData.height) data.push({ name: '当前', height: parseFloat(archiveData.height) });
    if (archiveData.futureHeightTh1) data.push({ name: 'Th1', height: parseFloat(archiveData.futureHeightTh1) });
    if (archiveData.futureHeightTh2) data.push({ name: 'Th2', height: parseFloat(archiveData.futureHeightTh2) });
    if (archiveData.futureHeightTh3) data.push({ name: 'Th3', height: parseFloat(archiveData.futureHeightTh3) });
    if (archiveData.futureHeight) data.push({ name: '预测', height: parseFloat(archiveData.futureHeight) });
    
    return data;
  };

  const renderHeightPredictionChart = () => {
    const data = getHeightPredictionData();
    
    if (data.length === 0) return null;

    return (
      <div style={{ height: 300 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={data}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="height" stroke="#8884d8" activeDot={{ r: 8 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const renderExpertAdvice = () => {
    if (!archiveData) return null;

    return (
      <Card title="专家建议" extra={!editing ? <Button onClick={() => setEditing(true)}>编辑</Button> : null}>
        {!editing ? (
          <div dangerouslySetInnerHTML={{ __html: archiveData.expertAdvice }} />
        ) : (
          <>
            <Form.Item name="expertAdvice" noStyle>
              <ReactQuill theme="snow" style={{ height: '200px', marginBottom: '40px' }} />
            </Form.Item>
          </>
        )}
      </Card>
    );
  };

  return (
    <div className="menu-item-content">
      <Spin spinning={loading}>
        <Card title="学员档案" extra={
          <div>
            {!editing ? (
              <Button onClick={() => setEditing(true)}>编辑</Button>
            ) : (
              <div>
                <Button type="primary" onClick={() => form.submit()}>保存</Button>
                <Button onClick={() => setEditing(false)} style={{ marginLeft: 8 }}>取消</Button>
              </div>
            )}
            <Button onClick={() => navigate(-1)} style={{ marginLeft: 8 }}>返回</Button>
          </div>
        }>
          {archiveData ? (
            <Form form={form} onFinish={onFinish} layout="vertical">
              {renderBasicInfo()}
              {renderAssessmentResults()}
              {renderPersonalInfo()}
              {renderExpertAdvice()}
              {renderHeightPredictionChart()}
              {editing && (
                <div style={{ textAlign: 'right', marginTop: 16 }}>
                  <Button type="primary" onClick={() => form.submit()}>保存</Button>
                  <Button onClick={() => setEditing(false)} style={{ marginLeft: 8 }}>取消</Button>
                </div>
              )}
            </Form>
          ) : (
            !loading && <div>暂无档案数据</div>
          )}
        </Card>
      </Spin>
    </div>
  );
};

export default StudentArchive;